import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Calendar, Users, UserCircle, Clock, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_clients: 0,
    total_employees: 0,
    total_appointments: 0,
    pending_appointments: 0,
    today_appointments: 0,
    unread_notifications: 0
  });

  const [notifications, setNotifications] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      // Fetch stats
      const statsRes = await axios.get(`${API_URL}/api/dashboard/stats`);
      setStats(statsRes.data);

      // Fetch notifications
      const notifsRes = await axios.get(`${API_URL}/api/notifications?recipient_id=admin`);
      setNotifications(notifsRes.data.slice(0, 5));

      // Fetch today's appointments
      const apptsRes = await axios.get(`${API_URL}/api/appointments`);
      const today = new Date().toISOString().split('T')[0];
      const todayAppts = apptsRes.data.filter(apt => 
        apt.appointment_date.startsWith(today)
      ).slice(0, 5);
      setTodayAppointments(todayAppts);

      setLoading(false);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const markNotificationRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error marking notification as read:', error);
    }
  };

  const statCards = [
    {
      title: 'Total Clients',
      value: stats.total_clients,
      icon: UserCircle,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Team Members',
      value: stats.total_employees,
      icon: Users,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: "Today's Appointments",
      value: stats.today_appointments,
      icon: Calendar,
      color: 'from-amber-500 to-amber-600'
    },
    {
      title: 'Pending',
      value: stats.pending_appointments,
      icon: Clock,
      color: 'from-rose-500 to-rose-600'
    }
  ];

  const getServiceIcon = (service) => {
    const icons = {
      nails: '💅',
      spray_tan: '☀️',
      facial: '✨',
      hair: '💇',
      makeup: '💄'
    };
    return icons[service] || '💎';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      emergency: 'bg-red-500/10 text-red-600 border-red-500/20',
      high: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
      normal: 'bg-blue-500/10 text-blue-600 border-blue-500/20'
    };
    return colors[priority] || colors.normal;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back, Christina! ✨ Get Your 24K Glow</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="relative p-2 rounded-lg hover:bg-accent transition-colors">
            <Bell className="w-5 h-5" />
            {stats.unread_notifications > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {stats.unread_notifications}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.title} className="overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">{stat.title}</p>
                  <p className="text-3xl font-bold mt-2">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Appointments */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-500" />
              Today's Appointments
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayAppointments.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No appointments today 🎉</p>
            ) : (
              <div className="space-y-3">
                {todayAppointments.map((apt) => (
                  <div key={apt.id} className="flex items-center justify-between p-3 bg-accent/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getServiceIcon(apt.service_type)}</span>
                      <div>
                        <p className="font-medium">{apt.client_name}</p>
                        <p className="text-sm text-muted-foreground capitalize">
                          {apt.service_type.replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-1 rounded-full border ${getPriorityColor(apt.priority)}`}>
                        {apt.priority}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        {apt.assigned_to_name || 'Unassigned'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-500" />
              Recent Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {notifications.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No new notifications</p>
            ) : (
              <div className="space-y-3">
                {notifications.map((notif) => (
                  <div 
                    key={notif.id} 
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      notif.read ? 'bg-accent/30' : 'bg-blue-500/10 border border-blue-500/20'
                    }`}
                    onClick={() => !notif.read && markNotificationRead(notif.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{notif.title}</p>
                        <p className="text-sm text-muted-foreground mt-1">{notif.message}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(notif.created_at).toLocaleString()}
                        </p>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg hover:shadow-lg transition-all">
              <span className="text-2xl mb-2 block">📅</span>
              <span className="text-sm font-medium">New Appointment</span>
            </button>
            <button className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all">
              <span className="text-2xl mb-2 block">👥</span>
              <span className="text-sm font-medium">Add Client</span>
            </button>
            <button className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all">
              <span className="text-2xl mb-2 block">✅</span>
              <span className="text-sm font-medium">Create Task</span>
            </button>
            <button className="p-4 bg-gradient-to-br from-rose-500 to-rose-600 text-white rounded-lg hover:shadow-lg transition-all">
              <span className="text-2xl mb-2 block">🚨</span>
              <span className="text-sm font-medium">Emergency</span>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;